/**
 * Analytics for reasoning panel interactions.
 * Currently logs to console; structured for future extension to Supabase or third-party services.
 */

import type { ReasoningData, MessageType } from "@/lib/types/reasoning"

/**
 * Event payload for reasoning panel opened event.
 */
export interface ReasoningPanelOpenedEvent {
  event: "reasoning_panel_opened"
  messageId: string
  messageType: MessageType
  reasoningContentPresent: {
    memoriesUsed: boolean
    keyFactors: boolean
    assumptions: boolean
    safetyCheck: boolean
  }
  timestamp: string
}

/**
 * Determines the message type based on available signals.
 * Falls back to "general" if unclear.
 */
export function inferMessageType(options: {
  isProfileMode?: boolean
  hasStrategyContext?: boolean
  messageContent?: string
}): MessageType {
  const { isProfileMode, hasStrategyContext, messageContent } = options

  if (isProfileMode) return "profile"
  if (hasStrategyContext) return "strategy"

  // Check for strategy keywords in content
  if (messageContent) {
    const strategyKeywords = ["strategy", "recommend", "advice", "plan", "goal", "growth"]
    const lowerContent = messageContent.toLowerCase()
    if (strategyKeywords.some((kw) => lowerContent.includes(kw))) {
      return "strategy"
    }
  }

  return "general"
}

/**
 * Logs when the reasoning panel is expanded.
 * Currently console-only; structured for future extension.
 *
 * @param messageId - Unique identifier for the message
 * @param messageType - Type of message (profile, general, strategy)
 * @param reasoning - The reasoning data being displayed
 */
export function logReasoningPanelOpened(messageId: string, messageType: MessageType, reasoning?: ReasoningData): void {
  const event: ReasoningPanelOpenedEvent = {
    event: "reasoning_panel_opened",
    messageId,
    messageType,
    reasoningContentPresent: {
      memoriesUsed: !!(reasoning?.memoriesUsed && reasoning.memoriesUsed.length > 0),
      keyFactors: !!(reasoning?.keyFactors && reasoning.keyFactors.length > 0),
      assumptions: !!(reasoning?.assumptions && reasoning.assumptions.length > 0),
      safetyCheck: !!reasoning?.safetyCheck,
    },
    timestamp: new Date().toISOString(),
  }

  // Development: Log to console
  if (process.env.NODE_ENV === "development") {
    console.log("[Analytics] Reasoning panel opened:", event)
  }

  // Future extension point: Send to Supabase
  // await supabase.from('analytics_events').insert(event)

  // Future extension point: Send to third-party analytics
  // posthog?.capture('reasoning_panel_opened', event)
  // mixpanel?.track('reasoning_panel_opened', event)
}

/**
 * Future: Log when a decision snapshot is created.
 * Placeholder for when decision snapshot feature is implemented.
 */
export function logDecisionSnapshotCreated(_snapshotId: string, _messageId: string, _messageType: MessageType): void {
  // Placeholder for future implementation
}
